import { Injectable } from '@angular/core';
import {
  Device,
  DeviceActionsService,
  DevicesService,
  IntegrationMDMProxyService,
  SyncService,
} from '@era/locations-sdk';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, mergeMap, take, tap, withLatestFrom } from 'rxjs/operators';
import {
  DeviceResolverActions,
  DeviceApiActions,
  DeviceUnassignViewActions,
  DeviceAssignViewActions,
  DeviceHousekeepingViewActions,
  DeviceStaffViewActions,
  DeviceFrontDeskViewActions,
  DeviceBarViewActions,
  DeviceKioskViewActions,
  DeviceSyncStatusViewActions,
  DevicesSyncModalActions,
  LocationSettingsWallpaperViewActions,
  BrandSettingsWallpaperViewActions,
  RoomSettingsWallpaperViewActions,
  DeviceApiWallpaperActions,
  DeviceActionsActions,
  LocationViewActions,
  BrandViewActions,
  GatewayApiActions,
} from './device.actions';
import { ExportService } from './export.service';
import { Router, RouterState } from '@angular/router';
import { DeviceService as OsUpdatesDeviceService } from '@era/osupdates-sdk';
import { Store } from '@ngrx/store';
import { DevicesRouterStoreSelectors, LocationsRouterStoreSelectors } from '../router';
import { getDevicesFilteredOnSubset } from './device.utils';
import { safeMap } from '../effects.helpers';

@Injectable()
export class DeviceEffects {
  constructor(
    private readonly deviceService: DevicesService,
    private readonly exportService: ExportService,
    private readonly routeStore: Store<RouterState>,
    private readonly integrationMDMProxyService: IntegrationMDMProxyService,
    private readonly actions$: Actions,
    private readonly deviceActionsService: DeviceActionsService,
    private readonly osUpdatesDeviceService: OsUpdatesDeviceService,
    private readonly devicesSyncService: SyncService,
    private readonly deviceStore: Store<Device>,
    private readonly routerStore: Store<Router>
  ) {}

  devicesLoadedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceResolverActions.devicesRequested),
      mergeMap(({ brandId, locationId }) =>
        safeMap(
          this.deviceService.getDevices(brandId, locationId),
          (devices: Device[]) => DeviceApiActions.deviceLoaded({ devices }),
          (error) => DeviceApiActions.deviceLoadedError({ error })
        )
      )
    );
  });

  exportLocationDevicesEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LocationViewActions.exportDevices),
      withLatestFrom(
        this.routerStore.select(LocationsRouterStoreSelectors.selectBrandId),
        this.routerStore.select(LocationsRouterStoreSelectors.selectLocationHumanId)
      ),
      mergeMap(([, brandId, locationId]) =>
        safeMap(
          this.exportService.getDeviceDetails(brandId, locationId, 'body').pipe(
            tap((res: Blob) => {
              // Create a new blob to add the file extension
              const blob = new Blob([res], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              });
              // Trigger the download
              const uri = window.URL.createObjectURL(blob);
              window.open(uri, 'download');
            })
          ),
          () => GatewayApiActions.exportDevicesSuccess(),
          (error) => GatewayApiActions.exportDevicesFailed({ error })
        )
      )
    );
  });

  exportBrandDevicesEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BrandViewActions.exportDevices),
      withLatestFrom(this.routerStore.select(LocationsRouterStoreSelectors.selectBrandId)),
      mergeMap(([, brandId]) =>
        safeMap(
          this.exportService.getDeviceDetails(brandId, undefined, 'body').pipe(
            tap((res: Blob) => {
              // Create a new blob to add the file extension
              const blob = new Blob([res], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              });
              // Trigger the download
              const uri = window.URL.createObjectURL(blob);
              window.open(uri, 'download');
            })
          ),
          () => GatewayApiActions.exportDevicesSuccess(),
          (error) => GatewayApiActions.exportDevicesFailed({ error })
        )
      )
    );
  });

  devicesForStateLoadedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceResolverActions.devicesForStateRequested),
      mergeMap(({ state }) =>
        safeMap(
          this.deviceService.getDevicesByDeviceState(state),
          (devices: Device[]) => DeviceApiActions.deviceLoaded({ devices }),
          (error) => DeviceApiActions.deviceLoadedError({ error })
        )
      )
    );
  });

  deviceUnassignedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceUnassignViewActions.deviceUnassigned),
      mergeMap(({ deviceLink }) =>
        safeMap(
          this.deviceActionsService.unassignDevice('default-brand', deviceLink),
          (device) => {
            // Optional properties are not removed, so we have to set them manually.
            device.locationUniqueId = null;
            device.roomUniqueId = null;
            device.housekeepingUniqueId = null;
            device.frontDeskUniqueId = null;
            device.barUniqueId = null;
            device.staffUniqueId = null;
            device.kioskUniqueId = null;
            return DeviceApiActions.deviceUnassigned({ device });
          },
          (error) => DeviceApiActions.deviceUnassignedError({ error })
        )
      )
    );
  });
  deviceMarkedAsUnAvailableEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceUnassignViewActions.deviceUnavailable),
      mergeMap(({ deviceLink }) =>
        safeMap(
          this.deviceActionsService.markDeviceAsUnavailable(deviceLink),
          (device) => {
            // Optional properties are not removed, so we have to set them manually.
            // Related to this: https://github.com/ngrx/platform/issues/1604
            device.locationUniqueId = null;
            device.roomUniqueId = null;
            device.housekeepingUniqueId = null;
            device.frontDeskUniqueId = null;
            device.barUniqueId = null;
            device.staffUniqueId = null;
            device.kioskUniqueId = null;
            return DeviceApiActions.deviceUnavailable({ device });
          },
          (error) => DeviceApiActions.deviceUnavailableError({ error })
        )
      )
    );
  });

  deviceAssignToHousekeeping$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceHousekeepingViewActions.deviceMarkedAsHousekeeping),
      mergeMap(({ link, locationId, brandId }) =>
        safeMap(
          this.deviceActionsService.assignHousekeepingDevice(locationId, brandId, link),
          (device) => {
            // Optional properties are not removed, so we have to set them manually.
            // Related to this: https://github.com/ngrx/platform/issues/1604
            device.roomUniqueId = null;
            device.staffUniqueId = null;
            device.frontDeskUniqueId = null;
            device.barUniqueId = null;
            device.staffUniqueId = null;
            device.kioskUniqueId = null;
            return DeviceApiActions.deviceMarkedAsHousekeeping({ device });
          },
          (error) => DeviceApiActions.deviceMarkedAsHousekeepingError({ error })
        )
      )
    );
  });

  deviceAssignToStaff$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceStaffViewActions.deviceMarkedAsStaff),
      mergeMap(({ link, locationId, brandId }) =>
        safeMap(
          this.deviceActionsService.assignStaffDevice(locationId, brandId, link),
          (device) => {
            // Optional properties are not removed, so we have to set them manually.
            // Related to this: https://github.com/ngrx/platform/issues/1604
            device.roomUniqueId = null;
            device.housekeepingUniqueId = null;
            device.frontDeskUniqueId = null;
            device.barUniqueId = null;
            device.kioskUniqueId = null;
            return DeviceApiActions.deviceMarkedAsStaff({ device });
          },
          (error) => DeviceApiActions.deviceMarkedAsStaffError({ error })
        )
      )
    );
  });

  deviceAssignToFrontDesk$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceFrontDeskViewActions.deviceMarkedAsFrontDesk),
      mergeMap(({ link, locationId, brandId }) =>
        safeMap(
          this.deviceActionsService.assignFrontDeskDevice(locationId, brandId, link),
          (device) => {
            // Optional properties are not removed, so we have to set them manually.
            // Related to this: https://github.com/ngrx/platform/issues/1604
            device.roomUniqueId = null;
            device.housekeepingUniqueId = null;
            device.staffUniqueId = null;
            device.barUniqueId = null;
            device.kioskUniqueId = null;
            return DeviceApiActions.deviceMarkedAsFrontDesk({ device });
          },
          (error) => DeviceApiActions.deviceMarkedAsFrontDeskError({ error })
        )
      )
    );
  });

  deviceAssignToBar$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceBarViewActions.deviceMarkedAsBar),
      mergeMap(({ link, locationId, brandId }) =>
        safeMap(
          this.deviceActionsService.assignBarDevice(locationId, brandId, link),
          (device) => {
            // Optional properties are not removed, so we have to set them manually.
            // Related to this: https://github.com/ngrx/platform/issues/1604
            device.roomUniqueId = null;
            device.housekeepingUniqueId = null;
            device.staffUniqueId = null;
            device.frontDeskUniqueId = null;
            device.kioskUniqueId = null;
            return DeviceApiActions.deviceMarkedAsBar({ device });
          },
          (error) => DeviceApiActions.deviceMarkedAsBarError({ error })
        )
      )
    );
  });

  deviceAssignToKiosk$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceKioskViewActions.deviceMarkedAsKiosk),
      mergeMap(({ link, locationId, brandId }) =>
        safeMap(
          this.deviceActionsService.assignKioskDevice(locationId, brandId, link),
          (device) => {
            // Optional properties are not removed, so we have to set them manually.
            // Related to this: https://github.com/ngrx/platform/issues/1604
            device.roomUniqueId = null;
            device.housekeepingUniqueId = null;
            device.staffUniqueId = null;
            device.frontDeskUniqueId = null;
            device.barUniqueId = null;
            return DeviceApiActions.deviceMarkedAsKiosk({ device });
          },
          (error) => DeviceApiActions.deviceMarkedAsKioskError({ error })
        )
      )
    );
  });

  deviceAssignedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceAssignViewActions.deviceAssigned),
      mergeMap(({ deviceLink, locationId, roomId, brandId }) =>
        safeMap(
          this.deviceActionsService.assignDevice(locationId, roomId, brandId, deviceLink),
          (device) => {
            // Optional properties are not removed, so we have to set them manually.
            // Related to this: https://github.com/ngrx/platform/issues/1604
            device.housekeepingUniqueId = null;
            device.staffUniqueId = null;
            device.frontDeskUniqueId = null;
            device.barUniqueId = null;
            device.kioskUniqueId = null;
            return DeviceApiActions.deviceAssigned({ device });
          },
          (error) => DeviceApiActions.deviceAssignedError({ error })
        )
      )
    );
  });

  deviceMarkedAsSpareEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceAssignViewActions.deviceMarkedAsSpare),
      mergeMap(({ deviceLink, locationId, brandId }) =>
        safeMap(
          this.deviceActionsService.markDeviceAsSpare(locationId, brandId, deviceLink),
          (device) => {
            // Optional properties are not removed, so we have to set them manually.
            // Related to this: https://github.com/ngrx/platform/issues/1604
            device.roomUniqueId = null;
            device.housekeepingUniqueId = null;
            device.staffUniqueId = null;
            device.frontDeskUniqueId = null;
            device.barUniqueId = null;
            device.kioskUniqueId = null;
            return DeviceApiActions.deviceMarkedAsSpare({ device });
          },
          (error) => DeviceApiActions.deviceMarkedAsSpareError({ error })
        )
      )
    );
  });

  deviceSyncRetryEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceSyncStatusViewActions.retrySync),
      mergeMap(({ udid, forceSync }) =>
        safeMap(
          this.deviceActionsService.syncDevice(udid, 'Apple', forceSync),
          () => DeviceApiActions.syncRetried({ udid }),
          (error) => DeviceApiActions.deviceSyncRetryError({ error })
        )
      )
    );
  });

  deviceRetryGivenSyncEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceSyncStatusViewActions.retryGivenSync),
      withLatestFrom(this.routerStore.select(DevicesRouterStoreSelectors.selectUdid)),
      mergeMap(([{ syncType, forceSync }, udid]) =>
        safeMap(
          this.deviceActionsService.syncDevice(udid, 'Apple', forceSync, [syncType]),
          () => DeviceApiActions.givenSyncRetried({ udid, syncType }),
          (error) => DeviceApiActions.deviceGivenSyncRetryError({ error })
        )
      )
    );
  });

  devicesSyncRequestedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DevicesSyncModalActions.syncDevicesRequested),
      mergeMap(({ locationSubset, force, syncTypes }) =>
        getDevicesFilteredOnSubset(this.deviceStore, locationSubset).pipe(
          take(1),
          map((devices: Device[]) => devices.map((device) => device.deviceId)),
          mergeMap((deviceIds: string[]) =>
            safeMap(
              this.devicesSyncService.syncDevices(force, syncTypes, { selector: { devices: deviceIds } }),
              () => DeviceApiActions.syncDevicesRequestSuccessful({ udids: deviceIds, syncTypes }),
              (error) => DeviceApiActions.deviceGivenSyncRetryError({ error })
            )
          )
        )
      )
    );
  });

  deviceOsUpdateResetEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceSyncStatusViewActions.retryOsUpdate),
      mergeMap(({ udid }) =>
        safeMap(
          this.osUpdatesDeviceService.resetOsUpdateState(udid),
          () => DeviceApiActions.oSUpdateRetried({ udid }),
          (error) => DeviceApiActions.deviceOsUpdateRetryError({ error })
        )
      )
    );
  });

  deviceOsUpdateContinueEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceSyncStatusViewActions.continueOsUpdate),
      mergeMap(({ udid }) =>
        safeMap(
          this.osUpdatesDeviceService.scheduleOsUpdate(udid),
          () => DeviceApiActions.oSUpdateContinued({ udid }),
          (error) => DeviceApiActions.deviceOsUpdateContinueError({ error })
        )
      )
    );
  });

  deviceWallpaperEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        LocationSettingsWallpaperViewActions.wallpaperCommandSent,
        BrandSettingsWallpaperViewActions.wallpaperCommandSent,
        RoomSettingsWallpaperViewActions.wallpaperCommandSent
      ),
      mergeMap(({ wallpaper, brandUniqueId, locationUniqueId, roomUniqueId }) =>
        safeMap(
          this.integrationMDMProxyService.queueSetWallpaperCommand(
            wallpaper.image,
            wallpaper.where,
            brandUniqueId,
            locationUniqueId,
            roomUniqueId
          ),
          () => DeviceApiWallpaperActions.wallpaperSuccessfullySet(),
          (error) => DeviceApiWallpaperActions.wallpaperFailedToSet({ error })
        )
      )
    );
  });

  deviceSetNameEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceActionsActions.setDesiredName),
      mergeMap(({ deviceId, newName }) =>
        safeMap(
          this.deviceActionsService.setDeviceName({
            deviceId: deviceId,
            deviceType: 'Apple',
            deviceName: newName,
          }),
          (device) => DeviceActionsActions.setDesiredNameSucceeded({ device }),
          (error) => DeviceActionsActions.setDesiredNameFailed({ error })
        )
      )
    );
  });
}
